import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { getTutorials } from "../../../store/tutorials";
import { getReportDetails, setLastPhotoNumber } from "../../../store/reportDetails";
import { getCoverData } from "../../../store/coverData";

import { getCurrentUser } from "../../../services/authService";
import { DEFAULT_REPORT_TITLE } from "../../../utils/constants";
import { PDFImageProps, PIDData, User } from "../../../utils/models";
import { tutorialConstants } from '../../../utils/constants';
import { matchPhrases, reArrangePhotoRecord, validateWorkOrderNumber } from "../../../utils/utilities";
import { SWUTItem } from "../../forms/custom-swut/swutItemForm";

import Modal from "../../themed/modal";
import Button from "../../themed/button";
import Elevation from "../../themed/instruction-modals/elevation";
import LoadingBadge from "../../themed/loading-badge";
import SWUTReport from "./";
import VIPhotoRecordForm from "../../forms/visual-inspection/viPhotoRecordForm";
import Conditional from "../../common/conditional";
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import Drawing from "../../forms/custom-lpt/drawing";
import ExecutiveSummaryForm from "../../forms/visual-inspection/executiveSummaryForm";
import FVICoverData from "../../themed/fvi-cover-data";
import FVIStructurePIDImageForm from "../../forms/custom-fvi/fviStructurePIDImageForm";
import MUTIsoPageForm from "../../forms/mut/mutIsoPageForm";
import ReportTitleForm from "../../forms/custom-fvi/reportTitleForm";
import SyncTime from "../../blocks/sync-time";
import SWUTReportItems from "./swutReportItems";
import Zippy from "../../themed/zippy";
import WarningNotice from "../custom-visual-inspection/warning-notice";

import useAutoSaveReport from "../../../hooks/useAutoSave";
import useProjectDetails from "../../../hooks/useProjectDetails";

import config from "../../../utils/config";
import toastService from "../../../services/toastService";

const CreateSWUTReport: React.FC = () => {
    const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const [previewReport, setPreviewReport] = useState(false);
    const [swutReports, setSWUTReports] = useState<SWUTItem[]>([]);
	const [coverData, setCoverData] = useState(fviCoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
    const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
    const [executiveSummary, setExecutiveSummary] = useState('');
    const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
    const [scanPlan, setScanPlan] = useState<PDFImageProps[]>([]);

    const reportDetails = useMemo(() => {
        return {
            coverData,
            reportTitle,
            executiveSummary,
            drawing,
            scanPlan,
            pidData,
            photoRecord,
            projectDetails,
            swutReports,
        }
    }, [coverData, reportTitle, drawing, executiveSummary, photoRecord, projectDetails, pidData, scanPlan, swutReports]);

    const validationMessage = useMemo(() => {
        if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

        if (matchPhrases(reportDetails.pidData.hasPID, 'yes') && reportDetails.pidData.pIDNumber.length === 0) {
            return 'Please provide at least one P&ID image!';
        }

        if (reportDetails.executiveSummary.length === 0) {
            return 'Please enter an executive summary!';
        }

        if (reportDetails.photoRecord.length === 0) {
            return 'Please provide at least one photo!';
        }

        if (reportDetails.scanPlan.length === 0) {
            return 'Please provinde at least one scan plan';
        }

        if (reportDetails.drawing.length === 0) {
            return 'Please upload at least one drawing!';
        }

        if (reportDetails.swutReports.length === 0) {
            return 'Please provide SWUT data!';
        }

        const isObservationMissing = reportDetails.swutReports.some((report) => report.observations.length === 0);
        if (isObservationMissing) return 'Please provide observation data!';
        
        const isProbeMissing = reportDetails.swutReports.some((report) => report.probes.length < 3);
        if (isProbeMissing) return 'Please provide at least 3 probe data!';


        return ''
    }, [reportDetails]);

    const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();
    const user = getCurrentUser() as User;

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

    const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

    useEffect(() => {
        function initializeCache() {
            const cachedReport = autoSave.oldReportData as typeof reportDetails;
            if (!cachedReport) {
                if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

                return;
            }
           
            setScanPlan(cachedReport.scanPlan);
            setCoverData(cachedReport.coverData);
            setReportTitle(cachedReport.reportTitle);
            setExecutiveSummary(cachedReport.executiveSummary);
            setDrawing(cachedReport.drawing);
            setPIDData(cachedReport.pidData);
            setSWUTReports(cachedReport.swutReports)
            setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			
            dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
        }

        initializeCache();
    }, [autoSave.oldReportData, dispatch, fviCoverData]);

    return ( 
        <Fragment>
            <Modal isVisible={previewReport} onClose={togglePreviewReport}>
                <SWUTReport {...reportDetails} />
            </Modal>

            <div className="mb-4">
                <SyncTime lastSynced={autoSave.lastSaved} />
            </div>

            {projectDetails && (
                <FVICoverData
                    coverDataId={config.coverDataTemplates.swut}
                    isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.swut && user.role.approveReports}
                    subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
                />
            )}

            <Conditional isVisible={validationMessage.length > 0}>
                <WarningNotice title='Incomplete Report' body={validationMessage} />
            </Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

            <Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

            <Zippy headerTitle="Drawing" isValid={drawing.length > 0}>
                <Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

            <Zippy headerTitle="Scan Plan" isValid={scanPlan.length > 0}>
				<MUTIsoPageForm initialImages={scanPlan} onProceed={setScanPlan} />
			</Zippy>
            
            <Zippy headerTitle="SW Report" isValid={swutReports.length > 0}>
                <SWUTReportItems items={swutReports} onDataChange={setSWUTReports} />
			</Zippy>

            <Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
        </Fragment>
     );
};

export default CreateSWUTReport;